/**
 * Requests Table Component - Hierarchical View
 *
 * Displays product master records with expandable child requests
 * Based on Podiq design reference with hierarchical navigation
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { formatDate, getStatusLabel } from '../../utils/formatters';
import ProductTypeIcon from '../ProductTypeIcon';
import ProductLinkSummaryModal from './ProductLinkSummaryModal';
import { TYPE_DISPLAY_NAMES, GPR_SUBTYPE_ANSWER_TO_CODE } from '../../config/requestTypeConstants';
import './RequestsTable.css';

const PRODUCT_TYPE_FILTER_OPTIONS = [
  { label: 'Technical Product', value: 'TECHNICAL' },
  { label: 'Commercial Product', value: 'COMMERCIAL' },
  { label: 'Package', value: 'PACKAGE' },
];

const isSkiReleaseReleased = (skiRelease) => {
  if (!skiRelease) return false;
  if (skiRelease.status === 'released') return true;
  if (skiRelease.milestone_ga_date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(skiRelease.milestone_ga_date) <= today;
  }
  return false;
};

const RequestsTable = ({
  groupedRequests,
  expandedProducts,
  loading,
  productTotal,
  filters,
  currentUser,
  products,
  showAllProducts,
  onShowAllProductsChange,
  showMyProducts,
  onShowMyProductsChange,
  onSearch,
  onFilterChange,
  onView,
  onEdit,
  onDelete,
  onEditProduct,
  onToggleProduct,
  onExpandAll,
  onCollapseAll,
}) => {
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [productTypeFilter, setProductTypeFilter] = useState('');
  const [linkSummaryProductId, setLinkSummaryProductId] = useState(null);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value === '') {
      onSearch('');
    }
  };

  const KEYWORD_FILTERS = [
    'linked', 'technical', 'commercial', 'package',
    'active', 'inactive', 'ea', 'ra', 'ga',
    'draft', 'verified',
  ];

  // Strip special keywords before sending to backend
  const getBackendSearchTerm = (term) => {
    return term.split(/\s+/)
      .filter(w => !KEYWORD_FILTERS.includes(w.toLowerCase()))
      .join(' ')
      .trim();
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    onSearch(getBackendSearchTerm(searchTerm));
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    onSearch('');
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      Draft: 'requests-table__badge--gray',
      in_progress: 'requests-table__badge--blue',
      submitted: 'requests-table__badge--yellow',
      completed: 'requests-table__badge--green',
      'Needs Input': 'requests-table__badge--orange',
    };
    return statusMap[status] || 'requests-table__badge--gray';
  };

  const getStatusIcon = (status) => {
    const icons = {
      Draft: '✏️',
      in_progress: '📝',
      submitted: '⚠️',
      completed: '📊',
    };
    return icons[status] || '';
  };

  // Outermost filters: ownership, then products-with-requests
  const visibleGroups = React.useMemo(() => {
    let groups = groupedRequests;
    if (showMyProducts && currentUser) {
      groups = groups.filter(g => g.product.owner?.id === currentUser.id);
    }
    return groups.filter(group =>
      showAllProducts ||
      group.product.product_type === 'PACKAGE' ||
      group.childCount > 0
    );
  }, [groupedRequests, showAllProducts, showMyProducts, currentUser]);

  const allExpanded = visibleGroups.length > 0 &&
    visibleGroups.every(g => expandedProducts.has(g.productId));

  if (loading && groupedRequests.length === 0) {
    return (
      <div className="requests-table__loading">
        <div className="requests-table__spinner" />
        <p>Loading requests...</p>
      </div>
    );
  }

  return (
    <div className="requests-table">
      {/* Filters Bar */}
      <div className="requests-table__filters">
        <form onSubmit={handleSearchSubmit} className="requests-table__search">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="requests-table__search-icon"
          >
            <path
              fillRule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clipRule="evenodd"
            />
          </svg>
          <input
            type="text"
            placeholder="Search products or requests..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="requests-table__search-input"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="requests-table__search-clear"
              title="Clear search"
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
          <div className="requests-table__search-help">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" className="requests-table__search-help-icon">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <div className="requests-table__search-tooltip">
              <p className="requests-table__search-tooltip-title">Search Tips</p>
              <p className="requests-table__search-tooltip-text">Type any text to search product names, request types, owner names, and more.</p>
              <p className="requests-table__search-tooltip-text">Combine keywords with spaces — all must match:</p>
              <table className="requests-table__search-tooltip-table">
                <tbody>
                  <tr><td><strong>Product Name</strong></td><td>any part of the product name</td></tr>
                  <tr><td><strong>Product Type</strong></td><td><code>technical</code> <code>commercial</code> <code>package</code></td></tr>
                  <tr><td><strong>Release Type</strong></td><td><code>ea</code> <code>ra</code> <code>ga</code></td></tr>
                  <tr><td><strong>Product Status</strong></td><td><code>active</code> <code>inactive</code></td></tr>
                  <tr><td><strong>Request Type</strong></td><td><code>CUC</code> <code>GPR</code> <code>PPP</code> <code>SOR</code> etc.</td></tr>
                  <tr><td><strong>Request Status</strong></td><td><code>draft</code> <code>verified</code></td></tr>
                  <tr><td><strong>Links</strong></td><td><code>linked</code></td></tr>
                </tbody>
              </table>
              <p className="requests-table__search-tooltip-example">e.g. <code>commercial ga linked</code> or <code>ClaimCenter CUC verified</code></p>
            </div>
          </div>
        </form>

        <div className="requests-table__expand-controls">
          <button
            className="requests-table__button requests-table__button--expand"
            onClick={allExpanded ? onCollapseAll : onExpandAll}
            title={allExpanded ? 'Collapse All' : 'Expand All'}
          >
            {allExpanded ? (
              <>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Collapse All
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
                Expand All
              </>
            )}
          </button>
        </div>

        <select
          className="requests-table__filter-select"
          value={productTypeFilter}
          onChange={(e) => setProductTypeFilter(e.target.value)}
          title="Filter by product type"
        >
          <option value="">All Product Types</option>
          {PRODUCT_TYPE_FILTER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {/* My Products toggle */}
        <div className="requests-table__toggle-control">
          <span className="requests-table__toggle-label">My Products</span>
          <div className="requests-table__toggle-info">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <div className="requests-table__toggle-tooltip">
              <p><strong>On:</strong> Shows only products where you are the assigned owner.</p>
              <p><strong>Off:</strong> Shows all products regardless of ownership.</p>
            </div>
          </div>
          <label className="requests-table__toggle-switch">
            <input
              type="checkbox"
              checked={showMyProducts}
              onChange={e => onShowMyProductsChange(e.target.checked)}
            />
            <span className="requests-table__toggle-slider" />
          </label>
        </div>

        {/* Show All Products toggle — visually separated */}
        <div className="requests-table__toggle-control">
          <span className="requests-table__toggle-label">Show All Products</span>
          <div className="requests-table__toggle-info">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <div className="requests-table__toggle-tooltip">
              <p><strong>On:</strong> All products are shown, including those with no active requests.</p>
              <p><strong>Off:</strong> Only products that have at least one request are shown. Packages are always visible.</p>
            </div>
          </div>
          <label className="requests-table__toggle-switch">
            <input
              type="checkbox"
              checked={showAllProducts}
              onChange={e => onShowAllProductsChange(e.target.checked)}
            />
            <span className="requests-table__toggle-slider" />
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="requests-table__wrapper">
        <table className="requests-table__table">
          <colgroup>
            <col style={{ width: '40px' }} />   {/* expand icon */}
            <col />                              {/* product / request type — fills remaining space */}
            <col style={{ width: '140px' }} />  {/* owner */}
            <col style={{ width: '175px' }} />  {/* actions + count badge */}
            <col style={{ width: '130px' }} />  {/* ski release */}
            <col style={{ width: '105px' }} />  {/* status */}
            <col style={{ width: '130px' }} />  {/* created by */}
            <col style={{ width: '110px' }} />  {/* created date */}
            <col style={{ width: '110px' }} />  {/* updated date */}
          </colgroup>
          <thead>
            <tr>
              <th className="requests-table__th requests-table__th--icon"></th>
              <th className="requests-table__th">Product / Request Type</th>
              <th className="requests-table__th">Owner</th>
              <th className="requests-table__th">Actions</th>
              <th className="requests-table__th">SKI Release</th>
              <th className="requests-table__th">Status</th>
              <th className="requests-table__th">Created By</th>
              <th className="requests-table__th">Created Date</th>
              <th className="requests-table__th">Updated Date</th>
            </tr>
          </thead>
          <tbody>
            {visibleGroups.length === 0 ? (
              <tr>
                <td colSpan="9" className="requests-table__empty">
                  No requests found
                </td>
              </tr>
            ) : (
              visibleGroups
                .filter(group => !productTypeFilter || group.product.product_type === productTypeFilter)
                .filter(group => {
                  if (!searchTerm.trim()) return true;
                  const keywords = searchTerm.trim().toLowerCase().split(/\s+/);
                  const typeMap = { technical: 'TECHNICAL', commercial: 'COMMERCIAL', package: 'PACKAGE' };
                  const releaseMap = { ea: 'EA', ra: 'RA', ga: 'GA' };
                  const ownerName = [
                    group.product.owner?.first_name || '',
                    group.product.owner?.last_name || '',
                  ].join(' ').toLowerCase();
                  const productName = (group.product.product_name || '').toLowerCase();

                  // Separate group-level special keywords from per-request keywords
                  const groupKeywords = keywords.filter(kw =>
                    kw === 'linked' || typeMap[kw] || releaseMap[kw] || kw === 'active' || kw === 'inactive'
                  );
                  const requestKeywords = keywords.filter(kw => !groupKeywords.includes(kw));

                  // Group-level checks must all pass
                  const groupPass = groupKeywords.every(kw => {
                    if (kw === 'linked') return group.product.linkedCount > 0;
                    if (typeMap[kw]) return group.product.product_type === typeMap[kw];
                    if (releaseMap[kw]) return group.product.release_type === releaseMap[kw];
                    if (kw === 'active') return group.product.status === 'active';
                    if (kw === 'inactive') return group.product.status === 'inactive';
                    return true;
                  });
                  if (!groupPass) return false;

                  if (requestKeywords.length === 0) return true;

                  // Products with no requests: match on product name / owner alone
                  // (status keywords like 'draft'/'verified' require a request to exist)
                  const hasStatusKeyword = requestKeywords.some(kw => ['draft', 'verified'].includes(kw));
                  if (!hasStatusKeyword && (group.children || []).length === 0) {
                    return requestKeywords.every(kw => productName.includes(kw) || ownerName.includes(kw));
                  }

                  // For remaining keywords (free-text + draft/verified): at least one request
                  // in the group must satisfy ALL of them simultaneously.
                  return (group.children || []).some(r => {
                    const rTypeName = (r.requestType?.type_name || '').toLowerCase();
                    const rTypeCode = (r.requestType?.type_code || '').toLowerCase();
                    const rRelease = (r.skiRelease?.release_name || r.skiRelease?.full_name || '').toLowerCase();
                    const rStatus = (r.status || '').toLowerCase();
                    return requestKeywords.every(kw => {
                      if (['draft', 'verified'].includes(kw)) return rStatus === kw;
                      return productName.includes(kw) || ownerName.includes(kw) ||
                        rTypeName.includes(kw) || rTypeCode.includes(kw) || rRelease.includes(kw);
                    });
                  });
                })
                .map((group) => {
                const isExpanded = expandedProducts.has(group.productId);
                return (
                  <React.Fragment key={group.productId}>
                    {/* Master Product Row */}
                    <tr
                      className="requests-table__row requests-table__row--master"
                      onClick={() => onToggleProduct(group.productId)}
                    >
                      <td className="requests-table__td requests-table__td--expand">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className={`requests-table__chevron ${isExpanded ? 'requests-table__chevron--expanded' : ''}`}
                        >
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </td>
                      <td className="requests-table__td requests-table__td--product">
                        <div className="requests-table__product-name">
                          <ProductTypeIcon productType={group.product.product_type} size={14} />
                          {group.product.linkedCount > 0 && (
                            <span
                              title="View linked products"
                              style={{ marginRight: 4, fontSize: 14, cursor: 'pointer' }}
                              onClick={(e) => { e.stopPropagation(); setLinkSummaryProductId(group.product.id); }}
                            >🔗</span>
                          )}
                          {group.product.product_name}
                        </div>
                        {group.product.categorization && (
                          <div className="requests-table__product-category">
                            {group.product.categorization}
                          </div>
                        )}
                      </td>
                      <td className="requests-table__td">
                        {group.product.owner ? (
                          <>
                            {group.product.owner.first_name} {group.product.owner.last_name}
                          </>
                        ) : (
                          'Unassigned'
                        )}
                      </td>
                      <td className="requests-table__td requests-table__actions">
                        <button
                          className="requests-table__action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            history.push(`/products/${group.product.id}`);
                          }}
                          title="View Product"
                        >
                          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        {currentUser?.role === 'admin' && (
                          <button
                            className="requests-table__action-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              history.push(`/products/${group.product.id}/edit`);
                            }}
                            title="Edit Product"
                          >
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                          </button>
                        )}
                        <span className="requests-table__badge requests-table__badge--count">
                          {group.childCount} {group.childCount === 1 ? 'request' : 'requests'}
                        </span>
                      </td>
                      <td className="requests-table__td"></td>
                      <td className="requests-table__td">
                        <div className="requests-table__product-status">
                          {group.product.status && (
                            <span
                              className="requests-table__badge"
                              style={{
                                backgroundColor:
                                  group.product.status === 'active' ? '#10b981' :
                                  group.product.status === 'inactive' ? '#6b7280' :
                                  group.product.status === 'deprecated' ? '#ef4444' : '#9ca3af',
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '500',
                                textTransform: 'capitalize',
                                display: 'inline-block',
                                marginBottom: '4px'
                              }}
                            >
                              {group.product.status}
                            </span>
                          )}
                          <div className="requests-table__status-summary">
                            {(() => {
                              const draftCount = group.statuses.Draft || 0;
                              const verifiedCount = group.statuses.Verified || 0;
                              return (
                                <>
                                  {draftCount > 0 && (
                                    <span className="requests-table__status-icon" title={`Draft: ${draftCount}`}>
                                      ✏️ {draftCount}
                                    </span>
                                  )}
                                  {verifiedCount > 0 && (
                                    <span className="requests-table__status-icon" title={`Verified: ${verifiedCount}`}>
                                      📊 {verifiedCount}
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </td>
                      <td className="requests-table__td"></td>
                      <td className="requests-table__td" colSpan="2"></td>
                    </tr>

                    {/* Child Request Rows */}
                    {isExpanded && group.children.map((request) => (
                      <tr key={request.id} className="requests-table__row requests-table__row--child">
                        <td className="requests-table__td requests-table__td--child-indent">
                          <span className="requests-table__connector">↳</span>
                        </td>
                        <td className="requests-table__td requests-table__td--id">
                          {(() => {
                            const typeCode = request.requestType?.type_code;
                            if (request.gpr_subtype) {
                              const subtypeCode = GPR_SUBTYPE_ANSWER_TO_CODE[request.gpr_subtype];
                              const subtypeLabel = (subtypeCode && TYPE_DISPLAY_NAMES[subtypeCode]) || request.gpr_subtype;
                              return `GPR - ${subtypeLabel}`;
                            }
                            if (typeCode === 'GPR' && request.answers) {
                              const gprSubtypeAnswer = request.answers.find(a => a.question_key === 'gpr_subtype');
                              if (gprSubtypeAnswer?.answer_value) {
                                const subtypeCode = GPR_SUBTYPE_ANSWER_TO_CODE[gprSubtypeAnswer.answer_value];
                                const subtypeLabel = (subtypeCode && TYPE_DISPLAY_NAMES[subtypeCode]) || gprSubtypeAnswer.answer_value;
                                return `GPR - ${subtypeLabel}`;
                              }
                              return TYPE_DISPLAY_NAMES[typeCode] || typeCode || 'N/A';
                            }
                            return TYPE_DISPLAY_NAMES[typeCode] || request.requestType?.type_name || 'N/A';
                          })()}
                        </td>
                        <td className="requests-table__td">
                          {request.owner ? (
                            <>
                              {request.owner.first_name} {request.owner.last_name}
                            </>
                          ) : (
                            'Unassigned'
                          )}
                        </td>
                        <td className="requests-table__td requests-table__actions">
                          {/* View button: always shown */}
                          <button
                            className="requests-table__action-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onView(request);
                            }}
                            title="View"
                          >
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                              <path
                                fillRule="evenodd"
                                d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                          {/* Edit button: disabled when ski release is released */}
                          {!isSkiReleaseReleased(request.skiRelease) && (
                          <button
                            className="requests-table__action-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(request);
                            }}
                            title="Edit"
                          >
                              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                            </button>
                          )}
                          {/* Delete button: draft only, owner or admin */}
                          {request.status === 'Draft' && (
                            currentUser?.role === 'admin' || currentUser?.id === request.created_by
                          ) && (
                            <button
                              className="requests-table__action-btn requests-table__action-btn--danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(request);
                              }}
                              title="Delete"
                            >
                              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                  fillRule="evenodd"
                                  d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                          )}
                        </td>
                        <td className="requests-table__td">
                          {request.skiRelease ? (
                            request.skiRelease.full_name || request.skiRelease.release_name
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="requests-table__td">
                          <span
                            className={`requests-table__badge ${getStatusBadgeClass(
                              request.status
                            )}`}
                          >
                            {getStatusLabel(request.status)}
                          </span>
                        </td>
                        <td className="requests-table__td">
                          {request.creator ? (
                            <>
                              {request.creator.first_name} {request.creator.last_name}
                            </>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="requests-table__td">{formatDate(request.created_at)}</td>
                        <td className="requests-table__td">{formatDate(request.updated_at)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>

        </table>
      </div>

      {/* Product count summary */}
      {productTotal > 0 && (
        <div className="requests-table__pagination">
          <div className="requests-table__pagination-info">
            {productTotal} {productTotal === 1 ? 'product' : 'products'} total
          </div>
        </div>
      )}
      {linkSummaryProductId && (
        <ProductLinkSummaryModal
          productId={linkSummaryProductId}
          onClose={() => setLinkSummaryProductId(null)}
        />
      )}
    </div>
  );
};

RequestsTable.propTypes = {
  groupedRequests: PropTypes.array.isRequired,
  expandedProducts: PropTypes.instanceOf(Set).isRequired,
  loading: PropTypes.bool.isRequired,
  productTotal: PropTypes.number,
  filters: PropTypes.object.isRequired,
  products: PropTypes.array,
  showAllProducts: PropTypes.bool.isRequired,
  onShowAllProductsChange: PropTypes.func.isRequired,
  showMyProducts: PropTypes.bool.isRequired,
  onShowMyProductsChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func,
  onView: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEditProduct: PropTypes.func.isRequired,
  onToggleProduct: PropTypes.func.isRequired,
  currentUser: PropTypes.object,
  onExpandAll: PropTypes.func.isRequired,
  onCollapseAll: PropTypes.func.isRequired,
};

export default RequestsTable;
